import type { Proposta, PropostaItem } from '@/types'
import type { LayoutConfig, PlanData } from '@/utils/dealCalculations'
import {
  calculateAllVariations,
  defaultLayoutConfig,
  getComboDurationFromItems,
} from '@/utils/dealCalculations'
import { normalizePropostaForExport, resolvePropostaItems } from '@/utils/normalizeProposta'
import { calculateComboValues } from '@/components/Deals/DealDetailPanel/DealProposalTab/comboPricing'
import type { ComboData } from '@/components/Deals/DealDetailPanel/DealProposalTab/types'
import { parseComboVariationsFromDados } from '@/components/Deals/DealDetailPanel/DealProposalTab/proposalStudioSave'
import { withComboPricingLayout } from '@/utils/comboSlideLayout'
import { formatCurrency } from '@/utils/format-currency'
import {
  buildExportPlanDataForItem,
  type BuildExportPlanDataOptions,
} from '@/utils/proposalExport'

export function applyComboFinalPricing(
  plan: PlanData,
  params: {
    valorMensalFinal: number
    valorMensalOriginal: number
    valorSetup?: number
    duracao?: number
    descontoPercentual?: number
    precoFechado?: number
  },
): PlanData {
  const mensalFinal = params.valorMensalFinal
  const mensalOriginal = params.valorMensalOriginal
  const hasDiscount = mensalOriginal > mensalFinal + 0.009
  const discountLabel =
    params.precoFechado !== undefined
      ? 'PREÇO ESPECIAL'
      : params.descontoPercentual && params.descontoPercentual > 0
        ? `${params.descontoPercentual}% OFF`
        : null
  const duracao = params.duracao || plan.pricing.duracao || 12
  const setup = params.valorSetup || 0

  return {
    ...plan,
    showBonus: hasDiscount,
    bonusText:
      hasDiscount && discountLabel
        ? `ECONOMIA DE ${formatCurrency(mensalOriginal - mensalFinal)} (${discountLabel})`
        : plan.bonusText,
    priceCompareFrom: hasDiscount ? mensalOriginal : undefined,
    pricing: {
      ...plan.pricing,
      valor: mensalFinal,
      valorMensal: mensalFinal,
      total: mensalFinal * duracao + setup,
      recorrente: true,
      duracao,
    },
    paymentDetails: `${duracao}x de ${formatCurrency(mensalFinal)}`,
    loyaltyDuration: `Fidelidade: ${duracao} meses`,
  }
}

export type ComboExportSlide = {
  plan: PlanData
  layout: LayoutConfig
  hidePricing: boolean
}

export function hasExplicitComboProposal(proposal: Proposta): boolean {
  if ((proposal.combos?.length ?? 0) > 0) return true
  return parseComboVariationsFromDados(proposal).length > 0
}

export function buildSeparateExportSlides(
  proposal: Proposta,
  options: BuildExportPlanDataOptions = {},
): ComboExportSlide[] {
  const normalized = normalizePropostaForExport(proposal)
  const items = resolvePropostaItems(normalized)
  return items.map((item) => {
    const plan = buildExportPlanDataForItem(normalized, item, options)
    const layout = plan.layoutConfig ?? defaultLayoutConfig
    return {
      plan: { ...plan, layoutConfig: layout },
      layout,
      hidePricing: false,
    }
  })
}

function buildComboSlideEntry(plan: PlanData, isLast: boolean): ComboExportSlide {
  const baseLayout = plan.layoutConfig ?? defaultLayoutConfig
  const layout = isLast ? withComboPricingLayout(baseLayout, plan.nome) : baseLayout
  return {
    plan: { ...plan, layoutConfig: layout },
    layout,
    hidePricing: !isLast,
  }
}

function orderItemsByIds(items: PropostaItem[], itemIds: string[]): PropostaItem[] {
  const ordered = itemIds
    .map((id) => items.find((i) => i.id === id))
    .filter((i): i is PropostaItem => !!i)
  return ordered.length > 0 ? ordered : items
}

function buildSlidesFromVariation(
  proposal: Proposta,
  items: PropostaItem[],
  variation: ReturnType<typeof calculateAllVariations>[number],
  options: BuildExportPlanDataOptions,
): ComboExportSlide[] {
  const ordered = orderItemsByIds(items, variation.itemIds)
  return ordered.map((item, idx) => {
    const isLast = idx === ordered.length - 1
    let plan = buildExportPlanDataForItem(proposal, item, options)
    if (isLast) {
      plan = applyComboFinalPricing(plan, {
        valorMensalFinal: variation.valorFinal,
        valorMensalOriginal: variation.valorOriginal,
        valorSetup: variation.valorSetup,
        duracao: getComboDurationFromItems(variation.items),
        descontoPercentual: variation.descontoPercentual,
        precoFechado: variation.precoFechado,
      })
    }
    return buildComboSlideEntry(plan, isLast)
  })
}

export function buildComboExportSlides(
  proposal: Proposta,
  options: BuildExportPlanDataOptions = {},
): ComboExportSlide[] {
  const normalized = normalizePropostaForExport(proposal)
  const items = resolvePropostaItems(normalized)
  if (items.length === 0) return []

  const studioVariations = parseComboVariationsFromDados(normalized)
  if (studioVariations.length > 0) {
    const priced = calculateAllVariations(studioVariations, items)
    const primary = priced[0]
    if (primary?.items.length) {
      return buildSlidesFromVariation(normalized, items, primary, options)
    }
  }

  const combos = (normalized.combos || []) as ComboData[]
  if (combos.length > 0) {
    const comboValues = calculateComboValues(combos, items)
    const cv = comboValues[0]
    const combo = combos[0]
    const ordered = orderItemsByIds(items, combo.itemIds)

    return ordered.map((item, idx) => {
      const isLast = idx === ordered.length - 1
      let plan = buildExportPlanDataForItem(normalized, item, options)
      if (isLast && cv) {
        const priced = calculateAllVariations(
          [
            {
              id: combo.id,
              nome: combo.nome,
              itemIds: combo.itemIds,
              descontoPercentual: combo.desconto,
            },
          ],
          items,
        )[0]
        plan = applyComboFinalPricing(plan, {
          valorMensalFinal: priced?.valorFinal ?? cv.total,
          valorMensalOriginal: priced?.valorOriginal ?? cv.subtotal,
          valorSetup: priced?.valorSetup,
          duracao: getComboDurationFromItems(ordered),
          descontoPercentual: combo.desconto,
        })
      }
      return buildComboSlideEntry(plan, isLast)
    })
  }

  return items.map((item, idx) => {
    const plan = buildExportPlanDataForItem(normalized, item, options)
    const isLast = idx === items.length - 1
    return buildComboSlideEntry(plan, isLast)
  })
}
